import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  renderBarcodeSvg,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_WITH_WORKSHOP_HEIGHT_MM,
  LABEL_WORKSHOP_CODE_HEIGHT_MM,
  LABEL_BARCODE_PREVIEW_JS_OPTIONS,
} from '../../utils/inventoryLabelBarcode';
import {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_FOLD_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from '../../utils/printInventoryLabels';

/** Preview: empty 44 mm + two panels of 22 mm (barcode | Plata/Bs). */
export interface InventoryLabelPreviewRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
}

export function InventoryLabelPreviewMini({ row }: { row: InventoryLabelPreviewRow }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const workshopCode = (row.workshopCode ?? '').trim();
  const talla = (row.diameterSize ?? '').trim();
  const hasWorkshop = workshopCode !== '';
  const barHeightMm = hasWorkshop
    ? LABEL_BARCODE_WITH_WORKSHOP_HEIGHT_MM
    : LABEL_BARCODE_SVG_MAX_HEIGHT_MM;

  useEffect(() => {
    renderBarcodeSvg(row.productCodeUnique ? svgRef.current : null, row.productCodeUnique ?? '', {
      ...LABEL_BARCODE_PREVIEW_JS_OPTIONS,
    });
  }, [row.productCodeUnique]);

  const priceStr = `Bs. ${Number(row.salePrice).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
  const weight =
    row.weightGrams != null && !Number.isNaN(Number(row.weightGrams))
      ? Number(row.weightGrams).toLocaleString('es-BO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '';

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${LABEL_PRINT_WIDTH_MM}mm`,
        height: `${LABEL_PRINT_HEIGHT_MM}mm`,
        overflow: 'hidden',
        bgcolor: '#fff',
        color: '#111827',
        fontFamily: '"Times New Roman", Times, serif',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_FOLD_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dotted',
          borderColor: 'grey.400',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_INFO_LEFT_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dashed',
          borderColor: 'grey.600',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_BARCODE_LEFT_MM}mm`,
          top: '0.5mm',
          bottom: '0.5mm',
          width: `${LABEL_BARCODE_WIDTH_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.3mm',
          overflow: 'hidden',
          px: '0.8mm',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '6pt',
            lineHeight: 1,
            width: '100%',
          }}
        >
          <Box component="span" sx={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>
            {row.productCodeUnique}
          </Box>
          {weight ? (
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {weight}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            height: `${barHeightMm}mm`,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <svg
            ref={svgRef}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: 'auto',
              maxWidth: '100%',
              height: `${barHeightMm}mm`,
              display: 'block',
            }}
          />
        </Box>
        {hasWorkshop ? (
          <Box
            sx={{
              height: `${LABEL_WORKSHOP_CODE_HEIGHT_MM}mm`,
              fontSize: '4pt',
              lineHeight: 1,
              fontFamily: 'Arial, Helvetica, sans-serif',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'clip',
            }}
          >
            {workshopCode}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_INFO_LEFT_MM}mm`,
          top: '0.5mm',
          bottom: '0.5mm',
          width: `${LABEL_INFO_WIDTH_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          px: '1mm',
          boxSizing: 'border-box',
        }}
      >
        <Typography sx={{ fontStyle: 'italic', fontSize: '9pt', lineHeight: 1.1 }} noWrap>
          {row.materialLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '0.5mm', overflow: 'hidden' }}>
          <Typography sx={{ fontSize: '9pt', lineHeight: 1.1 }} noWrap>
            {priceStr}
          </Typography>
          {talla ? (
            <Box
              component="span"
              sx={{
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: '4.5pt',
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {talla}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
